export const executeRateLimited = (
	msBeforeFirstExecution: number,
	minMsBetweenExecutions: number,
	{
		finalExecutionOnPageHide=false
	}: {
		finalExecutionOnPageHide?: boolean
	} = {}
) => (fn: () => void | Promise<void>) => {
	let lastExecutionTime = 0;
	let timeout: ReturnType<typeof setTimeout> | undefined;
	const execute = async () => {
		lastExecutionTime = Date.now();
		if (finalExecutionOnPageHide && window != null) {
			window.removeEventListener('pagehide', execute);
		}
		try {
			await fn();
		} finally {
			timeout = undefined;
		}
	}
	return () => {
		if (timeout) {
			// a future execution of the function is already scheduled.
			// No need to do anything
			return;
		}
		const now = Date.now();
		let msToWait = (lastExecutionTime + minMsBetweenExecutions) - now;
		if (msToWait <= 0) {
			// This is a new string of executions. Wait the minimum time before a first execution
			msToWait = msBeforeFirstExecution;
		}
		// This is an execution request recently after one is pending.
		timeout = setTimeout(execute, msToWait);
		if (finalExecutionOnPageHide && window != null) {
			window.addEventListener('pagehide', execute, { once: true });
		}
	}
}

interface NakedPromise<R> {
	promise: Promise<R>;
	resolve: (value: R) => void;
	reject: (reason?: unknown) => void;
}

export const createNakedPromise = <R>(): NakedPromise<R> => {
	let resolve: (value: R) => void;
	let reject: (reason?: unknown) => void;
	const promise = new Promise<R>((res, rej) => {
		resolve = res;
		reject = rej;
	});
	return { promise, resolve: resolve!, reject: reject! };
}

export function executeEventually<R>(this: unknown, fn: () => Promise<R>): () => Promise<R> {
	let currentExecutionsPromise: NakedPromise<R> | undefined = undefined;
	let subsequentExecutionPromise: NakedPromise<R> | undefined = undefined;
	return function(this: unknown): Promise<R> {
		// eslint-disable-next-line @typescript-eslint/no-this-alias
		const eeThis = this;
		if (subsequentExecutionPromise != null) {
			// The function is currently executing, and others are already awaiting
			// a subsequent execution. We can await the subsequent execution and know
			// that the function will have started after we requested it.
			return subsequentExecutionPromise.promise;
		} else if (currentExecutionsPromise != null) {
			// The function is currently executing, but may have started already
			// and be out of date. We want to await a subsequent execution.
			// Let's create a new promise to be resolved/rejected when that
			// subsequent execution completes.
			subsequentExecutionPromise = createNakedPromise<R>();
			return subsequentExecutionPromise.promise;
		}
		// The function is not currently executing. Let's start it,
		// and execute it...
		currentExecutionsPromise = createNakedPromise<R>();
		(async () => {
			while (currentExecutionsPromise != null) {
				try {
					const result = await fn.apply(eeThis);
					currentExecutionsPromise.resolve(result);
				} catch (e) {
					currentExecutionsPromise.reject(e);
				} finally {
					// If there were subsequent execution requests to execute the
					// function while we were running, potentially expecting the
					// function to process new data that arrived after it started
					// executing, we should execute it again.
					currentExecutionsPromise = subsequentExecutionPromise;
					subsequentExecutionPromise = undefined;
				}
			}
		})();
		return currentExecutionsPromise.promise;
	};
};
